import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ArrowLeft, Calendar, Clock, Tag, Share2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BlogPost = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPost();
  }, [id]);

  const fetchPost = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/blog/posts/${id}`);
      if (!response.ok) throw new Error('Post not found');
      const data = await response.json();
      setPost(data);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="pt-32 pb-20">
          <div className="max-w-4xl mx-auto px-8 lg:px-12 text-center">
            <p className="text-xl text-gray-600">Loading article...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="pt-32 pb-20">
          <div className="max-w-4xl mx-auto px-8 lg:px-12 text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Article Not Found</h1>
            <p className="text-xl text-gray-600 mb-8">The article you're looking for doesn't exist.</p>
            <button
              onClick={() => navigate('/blog')}
              className="inline-flex items-center px-6 py-3 bg-slate-700 hover:bg-slate-800 text-white font-semibold rounded-lg transition-colors"
            >
              <ArrowLeft className="mr-2 h-5 w-5" />
              Back to Insights
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="blog-post-page">
      <Header />
      
      {/* Hero Image */}
      {post.image_url && (
        <div className="relative h-[400px] w-full overflow-hidden">
          <img 
            src={post.image_url} 
            alt={post.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
        </div>
      )}
      
      {/* Article Header */}
      <article className={post.image_url ? "pt-12 pb-20" : "pt-32 pb-20"}>
        <div className="max-w-4xl mx-auto px-8 lg:px-12">
          {/* Back Button */}
          <button
            onClick={() => navigate('/blog')}
            className="inline-flex items-center text-slate-700 hover:text-slate-900 font-semibold mb-8 transition-colors"
          >
            <ArrowLeft className="mr-2 h-5 w-5" />
            Back to Insights
          </button>

          {/* Category Badge */}
          <div className="mb-6">
            <span className="inline-block px-4 py-2 bg-slate-100 rounded-full text-sm font-semibold text-slate-700">
              {post.category}
            </span>
          </div>

          {/* Title */}
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6 leading-tight">
            {post.title}
          </h1>

          {/* Meta Info */}
          <div className="flex flex-wrap items-center gap-6 text-gray-600 mb-8 pb-8 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              <span>{post.date}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              <span>{post.read_time}</span>
            </div>
          </div>

          {/* Excerpt */}
          {post.excerpt && (
            <div className="bg-slate-50 border-l-4 border-slate-700 p-6 mb-12">
              <p className="text-lg text-gray-700 leading-relaxed italic">
                {post.excerpt}
              </p>
            </div>
          )}

          {/* Article Content */}
          <div className="prose prose-lg max-w-none">
            <ReactMarkdown
              components={{
                h1: ({node, ...props}) => <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6" {...props} />,
                h2: ({node, ...props}) => <h3 className="text-2xl font-bold text-gray-900 mt-10 mb-4" {...props} />,
                h3: ({node, ...props}) => <h4 className="text-xl font-bold text-gray-900 mt-8 mb-3" {...props} />,
                p: ({node, ...props}) => <p className="text-gray-700 leading-relaxed mb-6" {...props} />,
                ul: ({node, ...props}) => <ul className="list-disc pl-6 mb-6 space-y-2" {...props} />,
                ol: ({node, ...props}) => <ol className="list-decimal pl-6 mb-6 space-y-2" {...props} />,
                li: ({node, ...props}) => <li className="text-gray-700 leading-relaxed" {...props} />,
                strong: ({node, ...props}) => <strong className="font-semibold text-gray-900" {...props} />,
                em: ({node, ...props}) => <em className="italic text-gray-700" {...props} />,
                blockquote: ({node, ...props}) => (
                  <blockquote className="border-l-4 border-slate-300 pl-6 italic text-gray-600 my-6" {...props} />
                ),
                a: ({node, ...props}) => (
                  <a className="text-slate-700 hover:text-slate-900 underline font-semibold" target="_blank" rel="noopener noreferrer" {...props} />
                ),
              }}
            >
              {post.content}
            </ReactMarkdown>
          </div>

          {/* Tags */}
          {post.tags && post.tags.length > 0 && (
            <div className="mt-12 pt-8 border-t border-gray-200">
              <div className="flex flex-wrap gap-3">
                {post.tags.map((tag, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-slate-50 rounded-lg text-sm text-slate-700 font-medium"
                  >
                    <Tag className="h-4 w-4" />
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </article>

      {/* CTA Section */}
      <section className="py-20 bg-slate-50 border-t border-gray-200">
        <div className="max-w-4xl mx-auto px-8 lg:px-12 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Have Questions About Trade Compliance?
          </h2>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            Our experts can help you navigate complex international trade regulations.
          </p>
          <a
            href="/contact"
            className="inline-flex items-center px-8 py-4 bg-slate-700 hover:bg-slate-800 text-white font-semibold rounded-lg transition-colors"
          >
            Contact Our Team
          </a>
        </div>
      </section>

      {/* Related Articles */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-8 lg:px-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Continue Reading</h2>
          <a
            href="/blog"
            className="inline-flex items-center text-slate-700 hover:text-slate-900 font-semibold transition-colors"
          >
            <ArrowLeft className="mr-2 h-5 w-5" />
            View All Insights
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogPost;
